import { useEffect, useMemo } from 'react';

import { OrderStatusType, PermissionType } from 'api/types/entity';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { usePermissions } from 'libs/permissions';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';
import { useFormContext } from 'modules/operation/common/forms/operationForm';

import { useToggleEditable } from '../../hooks/useToggleEditable';
import { useOperationContext } from '../../providers/OperationProvider';

enum ActionType {
  edit = 'edit',
  changeStatus = 'changeStatus',
  delete = 'delete',
}

const finishedStatuses = new Set([
  OrderStatusType.Cancelled,
  OrderStatusType.Executed,
  OrderStatusType.PartiallyExecuted,
  OrderStatusType.NotExecuted,
  OrderStatusType.Revoked,
]);

export const useOperationFormHeader = () => {
  const { enableEditable } = useToggleEditable();
  const { t } = useTranslation();
  const { checkPermissions } = usePermissions();

  const { onChangeStatus, onDelete, operation } = useOperationContext();

  const { readOnly, resetForm } = useFormContext();

  const editable = !readOnly;

  const actions = useMemo(() => {
    const isFinished = finishedStatuses.has(operation.order.status);
    return lodash.compact([
      checkPermissions([PermissionType.Operation_DeleteOne]) && {
        value: ActionType.delete,
        label: t('common.delete'),
        Icon: Icon.Bucket,
        color: Colors.red,
      },
      checkPermissions([PermissionType.Operation_SaveStatus]) && {
        value: ActionType.changeStatus,
        label: t('operation.operation.changeStatusLabel'),
        Icon: Icon.Repeat,
      },
      !isFinished &&
        checkPermissions([PermissionType.Operation_UpdateOne]) && {
          value: ActionType.edit,
          label: t('common.edit'),
          Icon: Icon.Edit,
        },
    ]);
  }, [checkPermissions, operation.order.status, t]);

  const onClickOption = (action: ActionType) => {
    switch (action) {
      case ActionType.edit:
        enableEditable();
        break;

      case ActionType.changeStatus:
        onChangeStatus();
        break;

      case ActionType.delete:
        onDelete();
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (!editable) {
      resetForm();
    }
  }, [editable, resetForm]);

  return {
    actions,
    editable,
    onClickOption,
  };
};
